import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import VanDuty from "@/pages/van/VanDuty";
import VanMap from "@/pages/van/VanMap";
import VanSettings from "@/pages/van/VanSettings";
import VanIgnition from "@/pages/van/VanIgnition";
import VanStockRequest from "@/pages/van/VanStockRequest";
import VanStockApprovals from "@/pages/van/VanStockApprovals";
import VanPerformance from "@/pages/van/VanPerformance";
import VanGeoMap from "@/pages/van/VanGeoMap";
import {
  Truck, Search, Plus, Trash2, Send, Save, CheckCircle2, XCircle, FileSignature,
  Package, UserPlus, Receipt, Loader2, ClipboardList, PackageOpen,
  BarChart3, FileText, MessageCircle, Wallet, Banknote, CreditCard, AlertTriangle, ArrowRightLeft,
  ClipboardCheck, MapPin, Settings, Stamp, KeyRound, PackagePlus, PackageCheck, TrendingUp, Map as MapIcon, Percent,
} from "lucide-react";

const fmtTL = (v) => `₺${Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS = {
  draft: { label: "Taslak", cls: "bg-slate-100 text-slate-700" },
  pending: { label: "Onayda", cls: "bg-amber-100 text-amber-800" },
  invoiced: { label: "Faturalandı", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-700" },
};

export default function VanSales() {
  const { user, can } = useAuth();
  const canApprove = can("van.approve") || can("approve.admin") || can("approve.chain") || can("accounting.write") || user?.is_owner;
  const canStockApprove = canApprove || can("van.stock.approve") || can("logistics.manage");
  const canRequest = can("van.stock.request") || can("van.sale");
  const [tab, setTab] = useState("new");

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6" data-testid="van-sales-page">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Truck className="h-6 w-6 text-brand-green" /> Sıcak Satış (Rota)
        </h1>
        <p className="text-sm text-slate-500">Sahada araçtan hızlı satış fişi oluştur, imzalat, merkez onayına gönder.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        <TabBtn id="new" cur={tab} set={setTab} icon={Plus} label="Yeni Sıcak Satış" testId="van-tab-new" />
        <TabBtn id="list" cur={tab} set={setTab} icon={ClipboardList} label={canApprove ? "Fişler & Onaylar" : "Fişlerim"} testId="van-tab-list" />
        <TabBtn id="ignition" cur={tab} set={setTab} icon={KeyRound} label="Mesai / Kontak" testId="van-tab-ignition" />
        {canRequest && <TabBtn id="stockreq" cur={tab} set={setTab} icon={PackagePlus} label="Depo Talebi" testId="van-tab-stockreq" />}
        {canStockApprove && <TabBtn id="stockappr" cur={tab} set={setTab} icon={PackageCheck} label="Stok Talepleri" testId="van-tab-stockappr" />}
        {canStockApprove && <TabBtn id="load" cur={tab} set={setTab} icon={PackageOpen} label="Araç Yükleme" testId="van-tab-load" />}
        {canApprove && <TabBtn id="duty" cur={tab} set={setTab} icon={ClipboardCheck} label="Şoför & Mutabakat" testId="van-tab-duty" />}
        {canApprove && <TabBtn id="reports" cur={tab} set={setTab} icon={BarChart3} label="Raporlar & Takip" testId="van-tab-reports" />}
        {canApprove && <TabBtn id="performance" cur={tab} set={setTab} icon={TrendingUp} label="Performans Panosu" testId="van-tab-performance" />}
        {canApprove && <TabBtn id="map" cur={tab} set={setTab} icon={MapPin} label="Rota Haritası" testId="van-tab-map" />}
        {canApprove && <TabBtn id="geomap" cur={tab} set={setTab} icon={MapIcon} label="Kontak & Bölge Haritası" testId="van-tab-geomap" />}
        {canApprove && <TabBtn id="settings" cur={tab} set={setTab} icon={Settings} label="Ayarlar" testId="van-tab-settings" />}
      </div>

      {tab === "new" && <NewSale onDone={() => setTab("list")} />}
      {tab === "list" && <SalesList canApprove={canApprove} />}
      {tab === "ignition" && <VanIgnition canApprove={canApprove} />}
      {tab === "stockreq" && canRequest && <VanStockRequest />}
      {tab === "stockappr" && canStockApprove && <VanStockApprovals />}
      {tab === "load" && canStockApprove && <VanLoad />}
      {tab === "duty" && canApprove && <VanDuty />}
      {tab === "reports" && canApprove && <Reports />}
      {tab === "performance" && canApprove && <VanPerformance />}
      {tab === "map" && canApprove && <VanMap />}
      {tab === "geomap" && canApprove && <VanGeoMap />}
      {tab === "settings" && canApprove && <VanSettings />}
    </div>
  );
}

function TabBtn({ id, cur, set, icon: Icon, label, testId }) {
  const active = cur === id;
  return (
    <button data-testid={testId} onClick={() => set(id)}
      className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active ? "border-brand-green text-brand-green" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

// ------------------ YENİ SATIŞ ------------------
function NewSale({ onDone }) {
  const { can, user } = useAuth();
  const canEditPrice = can("van.approve") || can("approve.admin") || can("approve.chain") || can("accounting.write") || user?.is_owner;
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [custQ, setCustQ] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [newMode, setNewMode] = useState(false);
  const [nc, setNc] = useState({ name: "", phone: "", tax_no: "", tax_office: "", province: "", district: "", address: "", type: "individual" });
  const [prodQ, setProdQ] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [custSig, setCustSig] = useState(null);
  const [spSig, setSpSig] = useState(null);
  const [note, setNote] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [allowedRates, setAllowedRates] = useState([3, 5]);
  const [collected, setCollected] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/van-sales/settings").then((r) => { const ar = r.data?.discount?.allowed_rates; if (Array.isArray(ar) && ar.length) setAllowedRates(ar.map(Number)); }).catch(() => {}); }, []);

  useEffect(() => {
    if (!custQ.trim()) { setCustResults([]); return; }
    const t = setTimeout(() => {
      api.get("/customers", { params: { q: custQ } }).then((r) => setCustResults((r.data || []).slice(0, 8))).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [custQ]);

  const searchProducts = useCallback(() => {
    api.get("/van-sales/products", { params: { q: prodQ, vehicle_id: vehicleId || undefined, limit: 30 } })
      .then((r) => setProdResults(r.data || [])).catch(() => {});
  }, [prodQ, vehicleId]);

  useEffect(() => {
    const t = setTimeout(searchProducts, 350);
    return () => clearTimeout(t);
  }, [searchProducts]);

  const addLine = (p) => {
    if (lines.find((l) => l.product_id === p.id)) { toast.info("Ürün zaten eklendi"); return; }
    setLines((ls) => [...ls, {
      product_id: p.id, sku: p.sku, name: p.name, unit: p.unit, vat_rate: p.vat_rate,
      list_price: p.route_price || 0, unit_price: p.route_price || 0, quantity: 1, discount_rate: 0,
    }]);
  };
  const updLine = (pid, field, val) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, [field]: val, ...(field === "unit_price" ? { discount_rate: 0 } : {}) } : l));
  const setLineDiscount = (pid, rate) => setLines((ls) => ls.map((l) => l.product_id === pid
    ? { ...l, discount_rate: rate, unit_price: rate > 0 ? Number((Number(l.list_price) * (1 - rate / 100)).toFixed(2)) : Number(l.list_price) }
    : l));
  const rmLine = (pid) => setLines((ls) => ls.filter((l) => l.product_id !== pid));

  const totals = useMemo(() => {
    let sub = 0, vat = 0, disc = 0, gross = 0;
    lines.forEach((l) => {
      const q = Number(l.quantity) || 0, up = Number(l.unit_price) || 0, lp = Number(l.list_price) || up;
      const lt = q * up;
      const net = l.vat_rate ? lt / (1 + l.vat_rate / 100) : lt;
      sub += net; vat += lt - net; gross += lt;
      if (lp > up) disc += (lp - up) * q;
    });
    return { sub, vat, gross, disc };
  }, [lines]);

  const validate = () => {
    if (!customer && !(newMode && nc.name.trim())) { toast.error("Müşteri seçin veya yeni müşteri adı girin"); return false; }
    if (lines.length === 0) { toast.error("En az bir ürün ekleyin"); return false; }
    return true;
  };

  const buildBody = (geoVal = null) => ({
    vehicle_id: vehicleId || null,
    customer_id: customer ? customer.id : null,
    new_customer: (!customer && newMode) ? nc : null,
    lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_price: Number(l.unit_price), list_price: Number(l.list_price), discount_rate: Number(l.discount_rate) || 0 })),
    note: note || null,
    payment_method: payMethod,
    collected_amount: payMethod === "veresiye" ? 0 : (collected === "" ? null : Number(collected)),
    geo: geoVal,
    customer_signature: custSig, salesperson_signature: spSig,
  });

  const validateVehicle = () => {
    if (!vehicleId) { toast.error("Araç seçin (rota takibi zorunlu)"); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validateVehicle() || !validate()) return;
    setBusy(true);
    try {
      await api.post("/van-sales", buildBody());
      toast.success("Taslak kaydedildi");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!validateVehicle() || !validate()) return;
    if (!custSig) { toast.error("Müşteri imzası gerekli"); return; }
    if (!spSig) { toast.error("Personel imzası gerekli"); return; }
    setBusy(true);
    try {
      let g = null;
      if (navigator.geolocation) {
        g = await new Promise((res) => navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 4000, maximumAge: 60000 }));
      }
      const { data } = await api.post("/van-sales", buildBody(g));
      await api.post(`/van-sales/${data.id}/submit`, {});
      toast.success("Fiş onaya gönderildi");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid gap-5 pb-28 lg:grid-cols-3 lg:pb-0">
      {!canEditPrice && <div className="lg:col-span-3"><MyVanSummary /></div>}
      <div className="space-y-4 lg:col-span-2">
        {/* Araç + Müşteri */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Araç (Rota)</Label>
              <select data-testid="van-vehicle-select" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                <option value="">Araç seç (opsiyonel)</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.driver_name ? ` · ${v.driver_name}` : ""} ({v.stock_items} çeşit)</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Müşteri</span>
                <button type="button" data-testid="van-newcust-toggle" onClick={() => { setNewMode((v) => !v); setCustomer(null); }}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-green">
                  <UserPlus className="h-3 w-3" /> {newMode ? "Kayıtlı müşteri seç" : "Yeni müşteri"}
                </button>
              </Label>
              {!newMode ? (
                customer ? (
                  <div data-testid="van-customer-selected" className="mt-1 flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                    <span className="font-medium text-emerald-900">{customer.name}</span>
                    <button onClick={() => setCustomer(null)} className="text-emerald-700 hover:text-red-600"><XCircle className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="relative mt-1">
                    <Input data-testid="van-customer-search" value={custQ} onChange={(e) => setCustQ(e.target.value)} placeholder="Müşteri ara…" />
                    {custResults.length > 0 && (
                      <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg">
                        {custResults.map((c) => (
                          <button key={c.id} data-testid={`van-customer-opt-${c.id}`} onClick={() => { setCustomer(c); setCustQ(""); setCustResults([]); }}
                            className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50">{c.name}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              ) : (
                <Input data-testid="van-newcust-name" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder="Yeni müşteri adı *" className="mt-1" />
              )}
            </div>
          </div>
          {newMode && (
            <div className="grid gap-2 sm:grid-cols-3" data-testid="van-newcust-form">
              <Input value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} placeholder="Telefon" data-testid="van-newcust-phone" />
              <Input value={nc.tax_no} onChange={(e) => setNc({ ...nc, tax_no: e.target.value })} placeholder="VKN / TCKN" data-testid="van-newcust-taxno" />
              <Input value={nc.province} onChange={(e) => setNc({ ...nc, province: e.target.value })} placeholder="İl" data-testid="van-newcust-province" />
              <Input value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} placeholder="Adres" className="sm:col-span-3" data-testid="van-newcust-address" />
            </div>
          )}
        </div>

        {/* Ürün ekle */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="van-product-search" value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Ürün ara (ad / SKU / barkod)…" className="pl-9" />
          </div>
          {prodResults.length > 0 && (
            <div className="mt-2 max-h-52 overflow-auto rounded-md border border-slate-100">
              {prodResults.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 border-b border-slate-50 px-3 py-2 text-sm last:border-0">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-slate-800">{p.name}</div>
                    <div className="text-[11px] text-slate-400">{p.sku} · Rota: {fmtTL(p.route_price)}{p.van_qty != null ? ` · Araçta: ${p.van_qty}` : ""}</div>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`van-add-${p.id}`} onClick={() => addLine(p)}><Plus className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          )}

          {lines.length > 0 && (
            <div className="mt-3 overflow-x-auto" data-testid="van-lines">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[11px] uppercase text-slate-400">
                    <th className="py-1.5">Ürün</th><th className="w-20">Adet</th>
                    <th className="w-28">Birim (₺)</th><th className="w-28">İskonto</th><th className="w-24 text-right">Rota</th>
                    <th className="w-24 text-right">Tutar</th><th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => {
                    const lt = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                    const discounted = Number(l.list_price) > Number(l.unit_price);
                    return (
                      <tr key={l.product_id} className="border-b border-slate-50" data-testid={`van-line-${l.product_id}`}>
                        <td className="py-1.5 pr-2"><div className="max-w-[180px] truncate font-medium text-slate-700">{l.name}</div></td>
                        <td><Input type="number" min="0" step="1" value={l.quantity} data-testid={`van-line-qty-${l.product_id}`}
                          onChange={(e) => updLine(l.product_id, "quantity", e.target.value)} className="h-8" /></td>
                        <td>{canEditPrice ? (
                          <Input type="number" min="0" step="0.01" value={l.unit_price} data-testid={`van-line-price-${l.product_id}`}
                            onChange={(e) => updLine(l.product_id, "unit_price", e.target.value)} className={`h-8 ${discounted ? "text-amber-700" : ""}`} />
                        ) : (
                          <div data-testid={`van-line-price-${l.product_id}`} className={`flex h-8 items-center px-1 text-sm font-medium ${discounted ? "text-amber-700" : "text-slate-700"}`}>{fmtTL(l.unit_price)}</div>
                        )}</td>
                        <td>
                          <div className="flex flex-wrap gap-0.5">
                            <button type="button" onClick={() => setLineDiscount(l.product_id, 0)} data-testid={`van-disc-${l.product_id}-0`}
                              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${!Number(l.discount_rate) ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-500"}`}>Rota</button>
                            {allowedRates.map((r) => (
                              <button key={r} type="button" onClick={() => setLineDiscount(l.product_id, r)} data-testid={`van-disc-${l.product_id}-${r}`}
                                className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${Number(l.discount_rate) === r ? "bg-amber-500 text-white" : "bg-amber-50 text-amber-700"}`}>%{r}</button>
                            ))}
                          </div>
                        </td>
                        <td className="text-right text-xs text-slate-400">{fmtTL(l.list_price)}</td>
                        <td className="text-right font-medium text-slate-800">{fmtTL(lt)}</td>
                        <td><button onClick={() => rmLine(l.product_id)} data-testid={`van-line-del-${l.product_id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {lines.length === 0 && <p className="mt-3 text-center text-sm text-slate-400">Henüz ürün eklenmedi.</p>}
        </div>

        {/* İmzalar */}
        <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-2">
          <SignaturePad label="Müşteri İmzası *" testId="van-sign-customer" onChange={setCustSig} />
          <SignaturePad label="Satışı Yapan Personel İmzası *" testId="van-sign-salesperson" onChange={setSpSig} />
        </div>
      </div>

      {/* Özet */}
      <div className="space-y-3">
        <div className="sticky top-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 flex items-center gap-2 font-semibold text-slate-800"><Receipt className="h-4 w-4" /> Fiş Özeti</h3>
          <Row label="Ara Toplam (net)" value={fmtTL(totals.sub)} />
          <Row label="KDV" value={fmtTL(totals.vat)} />
          {totals.disc > 0 && <Row label="Uygulanan İskonto" value={`- ${fmtTL(totals.disc)}`} cls="text-amber-700" testId="van-summary-discount" />}
          <div className="my-2 border-t border-slate-100" />
          <Row label="Genel Toplam" value={fmtTL(totals.gross)} strong testId="van-summary-gross" />
          <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50/60 p-2">
            <Label className="text-xs">Ödeme Yöntemi</Label>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {[["cash", "Nakit", Banknote], ["card", "POS/Kart", CreditCard], ["transfer", "Havale", ArrowRightLeft], ["veresiye", "Veresiye", Wallet]].map(([v, lbl, Icon]) => (
                <button key={v} type="button" data-testid={`van-pay-${v}`} onClick={() => setPayMethod(v)}
                  className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ${payMethod === v ? "border-brand-green bg-emerald-50 text-brand-green" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                  <Icon className="h-3.5 w-3.5" /> {lbl}
                </button>
              ))}
            </div>
            {payMethod !== "veresiye" && (
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">Tahsil edilen (₺)</span>
                <Input type="number" min="0" step="0.01" value={collected} onChange={(e) => setCollected(e.target.value)} placeholder={totals.gross.toFixed(2)} className="h-8 w-28" data-testid="van-collected" />
              </div>
            )}
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className="text-slate-500">Kalan (Veresiye · Cari)</span>
              <span className="font-semibold text-amber-700" data-testid="van-credit">{fmtTL(payMethod === "veresiye" ? totals.gross : Math.max(0, totals.gross - (collected === "" ? totals.gross : Number(collected) || 0)))}</span>
            </div>
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (opsiyonel)" className="mt-3 h-16 text-sm" data-testid="van-note" />
          <div className="mt-3 space-y-2">
            <Button className="w-full" onClick={submit} disabled={busy} data-testid="van-submit-btn">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Onaya Gönder
            </Button>
            <Button variant="outline" className="w-full" onClick={saveDraft} disabled={busy} data-testid="van-draft-btn">
              <Save className="mr-1 h-4 w-4" /> Taslak Kaydet
            </Button>
          </div>
        </div>
      </div>
      {/* Mobil hızlı gönder çubuğu — büyük buton, tek dokunuş (sahadaki şoför için) */}
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center gap-3 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur lg:hidden" data-testid="van-mobile-bar">
        <div className="flex-1">
          <div className="text-[11px] text-slate-400">Genel Toplam</div>
          <div className="text-lg font-bold text-slate-900" data-testid="van-mobile-total">{fmtTL(totals.gross)}</div>
        </div>
        <Button className="h-12 flex-1 text-base" onClick={submit} disabled={busy} data-testid="van-mobile-submit">
          {busy ? <Loader2 className="mr-1 h-5 w-5 animate-spin" /> : <Send className="mr-1 h-5 w-5" />} Onaya Gönder
        </Button>
      </div>
    </div>
  );
}

function MyVanSummary() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/van-sales/my-summary").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  const rights = d.discount_rights || 0;
  const full = rights && d.discount_used >= rights;
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="van-my-summary">
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-3" data-testid="my-today-sales">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700"><Banknote className="h-3.5 w-3.5" /> Bugünkü Satış</div>
        <div className="mt-1 text-base font-bold text-emerald-900 sm:text-lg">{fmtTL(d.today_sales)}</div>
        <div className="text-[11px] text-emerald-600">{d.today_count} fiş</div>
      </div>
      <div className={`rounded-xl border p-3 ${full ? "border-red-100 bg-red-50/70" : "border-amber-100 bg-amber-50/70"}`} data-testid="my-discount">
        <div className={`flex items-center gap-1.5 text-[11px] font-medium ${full ? "text-red-700" : "text-amber-700"}`}><Percent className="h-3.5 w-3.5" /> İskonto Hakkı</div>
        <div className={`mt-1 text-base font-bold sm:text-lg ${full ? "text-red-800" : "text-amber-900"}`}>{rights ? `${d.discount_used}/${rights}` : d.discount_used}</div>
        <div className={`text-[11px] ${full ? "text-red-600" : "text-amber-600"}`}>{rights ? (full ? "hak doldu" : `${d.discount_remaining} kaldı`) : "limitsiz"}</div>
      </div>
      <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-3" data-testid="my-est-prim">
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-sky-700"><TrendingUp className="h-3.5 w-3.5" /> Tahmini Prim</div>
        <div className="mt-1 text-base font-bold text-sky-900 sm:text-lg">{fmtTL(d.est_prim)}</div>
        <div className="text-[11px] text-sky-600">%{d.prim_pct} · kesinti öncesi</div>
      </div>
    </div>
  );
}

function Row({ label, value, strong, cls = "", testId }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm" data-testid={testId}>
      <span className={strong ? "font-semibold text-slate-800" : "text-slate-500"}>{label}</span>
      <span className={`${strong ? "text-lg font-bold text-slate-900" : "font-medium text-slate-700"} ${cls}`}>{value}</span>
    </div>
  );
}

// ------------------ LİSTE + ONAY ------------------
function SalesList({ canApprove }) {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/van-sales", { params: { status: status || undefined } })
      .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const tabs = [["pending", "Onayda"], ["draft", "Taslak"], ["invoiced", "Faturalandı"], ["rejected", "Reddedildi"], ["", "Tümü"]];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {tabs.map(([v, lbl]) => (
          <button key={v || "all"} data-testid={`van-filter-${v || "all"}`} onClick={() => setStatus(v)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${status === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 py-12 text-center text-sm text-slate-400" data-testid="van-list-empty">Kayıt yok.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="van-list">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
              <tr><th className="px-3 py-2">Fiş No</th><th>Müşteri</th><th>Personel</th><th className="text-right">Tutar</th><th>Durum</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-50 hover:bg-slate-50/50" data-testid={`van-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.doc_no}</td>
                  <td className="font-medium text-slate-800">{r.customer_name || r.new_customer?.name || "—"}{r.customer_pending && <span className="ml-1 rounded bg-indigo-100 px-1 text-[10px] text-indigo-700">yeni</span>}</td>
                  <td className="text-slate-500">{r.salesperson_name}</td>
                  <td className="text-right font-semibold text-slate-800">{fmtTL(r.gross_total)}</td>
                  <td><Badge className={`${STATUS[r.status]?.cls} border-0`}>{STATUS[r.status]?.label}</Badge></td>
                  <td className="pr-3 text-right">
                    <Button size="sm" variant="outline" data-testid={`van-open-${r.id}`} onClick={() => setDetail(r.id)}>Aç</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && <DetailDialog id={detail} canApprove={canApprove} onClose={() => setDetail(null)} onChanged={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function DetailDialog({ id, canApprove, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deduct, setDeduct] = useState(false);
  const [penalty, setPenalty] = useState("");
  const [mnote, setMnote] = useState("");

  useEffect(() => {
    api.get(`/van-sales/${id}`).then((r) => { setD(r.data); setLines(r.data.lines || []); setPenalty(String(r.data.discount_total || 0)); }).catch(() => {});
  }, [id]);

  if (!d) return null;
  const isPending = d.status === "pending";
  const canEdit = isPending && canApprove;

  const updLine = (pid, val) => setLines((ls) => ls.map((l) => l.product_id === pid ? { ...l, unit_price: val } : l));
  const gross = lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);

  const saveAdjust = async () => {
    setBusy(true);
    try {
      await api.put(`/van-sales/${id}`, {
        lines: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_price: Number(l.unit_price), list_price: Number(l.list_price) })),
      });
      toast.success("Fiş güncellendi");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/van-sales/${id}/approve`, {
        deduct_excess_discount: deduct, penalty_amount: deduct ? Number(penalty) : null, note: mnote || null,
      });
      toast.success(`Faturalandı · ${data.sale_no}`);
      if (data.penalty_requested && !data.penalty_applied) {
        toast.warning(data.penalty_note || "Kesinti uygulanamadı — satan kişi personel kaydına bağlı değil.");
      } else if (data.penalty_applied) {
        toast.info(data.penalty_note || `Personel bakiyesine ${fmtTL(data.penalty_amount)} kesinti yazıldı.`);
      }
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Onaylanamadı"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/van-sales/${id}/reject`, { note: mnote || null });
      toast.success("Fiş reddedildi");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="van-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-brand-green" /> {d.doc_no}
            <Badge className={`${STATUS[d.status]?.cls} border-0`}>{STATUS[d.status]?.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-1 text-sm sm:grid-cols-2">
          <div><span className="text-slate-400">Müşteri:</span> <span className="font-medium">{d.customer_name || d.new_customer?.name || "—"}</span></div>
          <div><span className="text-slate-400">Personel:</span> <span className="font-medium">{d.salesperson_name}</span></div>
          <div><span className="text-slate-400">Araç:</span> <span className="font-medium">{d.vehicle_plate || "—"}</span></div>
          {d.sale_no && <div><span className="text-slate-400">Satış No:</span> <span className="font-mono">{d.sale_no}</span></div>}
        </div>

        <div className="mt-2 overflow-x-auto rounded-lg border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
              <tr><th className="px-2 py-1.5">Ürün</th><th className="w-16">Adet</th><th className="w-28">Birim (₺)</th><th className="w-24 text-right">Rota</th><th className="w-24 text-right">Tutar</th></tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const disc = Number(l.list_price) > Number(l.unit_price);
                return (
                  <tr key={l.product_id} className="border-t border-slate-50">
                    <td className="px-2 py-1.5">{l.name}</td>
                    <td>{l.quantity} {l.unit}</td>
                    <td>{canEdit ? (
                      <Input type="number" min="0" step="0.01" value={l.unit_price} data-testid={`van-detail-price-${l.product_id}`}
                        onChange={(e) => updLine(l.product_id, e.target.value)} className={`h-8 ${disc ? "text-amber-700" : ""}`} />
                    ) : <span className={disc ? "text-amber-700" : ""}>{fmtTL(l.unit_price)}</span>}</td>
                    <td className="text-right text-xs text-slate-400">{fmtTL(l.list_price)}</td>
                    <td className="text-right font-medium">{fmtTL((Number(l.quantity) || 0) * (Number(l.unit_price) || 0))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">İskonto (rota fiyatına göre): <span className="font-medium text-amber-700">{fmtTL(d.discount_total)}</span></span>
          <span className="text-lg font-bold text-slate-900" data-testid="van-detail-gross">{fmtTL(canEdit ? gross : d.gross_total)}</span>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <SignaturePad label="Müşteri İmzası" testId="van-detail-sign-customer" value={d.customer_signature} readOnly />
          <SignaturePad label="Personel İmzası" testId="van-detail-sign-salesperson" value={d.salesperson_signature} readOnly />
        </div>

        {d.note && <p className="rounded-md bg-slate-50 p-2 text-sm text-slate-600">Saha notu: {d.note}</p>}
        {d.merkez_note && <p className="rounded-md bg-indigo-50 p-2 text-sm text-indigo-700">Merkez notu: {d.merkez_note}</p>}
        {d.excess_deducted && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">Fazla iskonto personelden düşüldü: {fmtTL(d.penalty_amount)}</p>}

        {canEdit && (
          <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
            <div className="flex items-start gap-2">
              <Checkbox id="van-deduct" checked={deduct} onCheckedChange={setDeduct} data-testid="van-deduct-check" />
              <div className="flex-1">
                <Label htmlFor="van-deduct" className="text-sm font-medium">Fazla / gereksiz iskontoyu satan personelin cari bakiyesinden düş</Label>
                {deduct && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-slate-500">Kesinti tutarı (₺):</span>
                    <Input type="number" min="0" step="0.01" value={penalty} onChange={(e) => setPenalty(e.target.value)} className="h-8 w-32" data-testid="van-penalty-input" />
                  </div>
                )}
              </div>
            </div>
            <Textarea value={mnote} onChange={(e) => setMnote(e.target.value)} placeholder="Merkez notu (opsiyonel)" className="h-16 text-sm" data-testid="van-merkez-note" />
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="van-pdf-btn" onClick={async () => {
            try { const r = await api.get(`/van-sales/${id}/pdf`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); window.open(u, "_blank"); }
            catch { toast.error("PDF açılamadı"); } }}>
            <FileText className="mr-1 h-4 w-4" /> PDF
          </Button>
          <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="van-wa-btn" onClick={async () => {
            try {
              const { data } = await api.get(`/van-sales/${id}/share`);
              const phone = (data.phone || "").replace(/\D/g, "").replace(/^0/, "90");
              const msg = `${data.customer_name || ""} — Rota Satış Fişi ${data.doc_no}\nTutar: ${fmtTL(data.gross_total)}${data.pdf_url ? "\nFiş (PDF): " + data.pdf_url : ""}`;
              window.open(phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
            } catch { toast.error("Paylaşım bağlantısı alınamadı"); } }}>
            <MessageCircle className="mr-1 h-4 w-4" /> WhatsApp
          </Button>
          {d.status === "invoiced" && d.sale_id && (
            <Button variant="outline" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="van-earsiv-btn" onClick={async () => {
              try { const r = await api.post(`/e-belge/invoice/${d.sale_id}`, { kind: "earchive", auto: true });
                toast.success(`e-Belge oluşturuldu (${r.data?.doc_no || r.data?.normalized_status || "taslak"})`); }
              catch (e) { toast.error(e?.response?.data?.detail || "e-Belge oluşturulamadı"); } }}>
              <Stamp className="mr-1 h-4 w-4" /> e-Arşiv
            </Button>
          )}
          {canEdit && <>
            <Button variant="outline" onClick={saveAdjust} disabled={busy} data-testid="van-adjust-save"><Save className="mr-1 h-4 w-4" /> Düzenlemeyi Kaydet</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="van-reject-btn"><XCircle className="mr-1 h-4 w-4" /> Reddet</Button>
            <Button onClick={approve} disabled={busy} data-testid="van-approve-btn">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Faturalaştır
            </Button>
          </>}
          {!canEdit && <Button variant="outline" onClick={onClose} data-testid="van-detail-close">Kapat</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------ RAPORLAR & TAKİP ------------------
function Reports() {
  const [period, setPeriod] = useState("month");
  const [veh, setVeh] = useState(null);
  const [sp, setSp] = useState(null);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/van-sales/reports/vehicles", { params: { period } }).then((r) => r.data).catch(() => null),
      api.get("/van-sales/reports/salespeople", { params: { period } }).then((r) => r.data).catch(() => null),
      api.get("/van-sales/reports/day").then((r) => r.data).catch(() => null),
    ]).then(([v, s, d]) => { setVeh(v); setSp(s); setDay(d); }).finally(() => setLoading(false));
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const periods = [["day", "Bugün"], ["week", "Bu Hafta"], ["month", "Bu Ay"], ["year", "Bu Yıl"], ["all", "Tümü"]];

  return (
    <div className="space-y-5" data-testid="van-reports">
      <div className="flex flex-wrap gap-2">
        {periods.map(([v, lbl]) => (
          <button key={v} data-testid={`van-period-${v}`} onClick={() => setPeriod(v)}
            className={`rounded-full px-3 py-1 text-xs font-medium ${period === v ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{lbl}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
        <>
          {/* Bugün kasa özeti */}
          {day && (
            <div className="grid gap-3 sm:grid-cols-4" data-testid="van-day-cards">
              <StatCard icon={Banknote} label="Bugün Nakit" value={fmtTL(day.totals.cash)} tone="emerald" />
              <StatCard icon={CreditCard} label="Bugün POS/Kart" value={fmtTL(day.totals.card)} tone="sky" />
              <StatCard icon={ArrowRightLeft} label="Bugün Havale" value={fmtTL(day.totals.transfer)} tone="violet" />
              <StatCard icon={Wallet} label="Bugün Veresiye" value={fmtTL(day.totals.credit)} tone="amber" />
            </div>
          )}

          {/* Araç faaliyeti */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800"><Truck className="h-4 w-4 text-brand-green" /> Araç Faaliyeti & Stok Takibi</div>
            <table className="w-full text-sm" data-testid="van-report-vehicles">
              <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
                <tr><th className="px-3 py-2">Araç</th><th>Şoför</th><th className="text-right">Yüklenen</th><th className="text-right">Satılan</th><th className="text-right">Ciro</th><th className="text-right">Nakit</th><th className="text-right">POS</th><th className="text-right">Havale</th><th className="text-right">Veresiye</th><th className="text-right">Kalan</th><th className="text-right">Kritik</th></tr>
              </thead>
              <tbody>
                {(veh?.vehicles || []).map((v) => (
                  <tr key={v.vehicle_id} className="border-t border-slate-50" data-testid={`van-report-veh-${v.vehicle_id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{v.plate || "—"}</td>
                    <td className="text-slate-500">{v.driver_name || "—"}</td>
                    <td className="text-right text-slate-500">{v.loaded_units}</td>
                    <td className="text-right text-slate-700">{v.sold_units}</td>
                    <td className="text-right font-semibold text-slate-800">{fmtTL(v.sold_amount)}</td>
                    <td className="text-right text-emerald-700">{fmtTL(v.cash)}</td>
                    <td className="text-right text-sky-700">{fmtTL(v.card)}</td>
                    <td className="text-right text-violet-700">{fmtTL(v.transfer)}</td>
                    <td className="text-right text-amber-700">{fmtTL(v.credit)}</td>
                    <td className={`text-right ${v.remaining_units < 0 ? "font-semibold text-red-600" : "text-slate-600"}`} title={v.remaining_units < 0 ? "Yüklemesiz/eksik satış — stok tutarsızlığı!" : ""}>
                      {v.remaining_units}{v.remaining_units < 0 && <AlertTriangle className="ml-0.5 inline h-3 w-3" />}
                    </td>
                    <td className="text-right">{v.low_items > 0 ? <span className="inline-flex items-center gap-0.5 rounded bg-red-100 px-1.5 text-xs font-medium text-red-700"><AlertTriangle className="h-3 w-3" />{v.low_items}</span> : <span className="text-slate-300">0</span>}</td>
                  </tr>
                ))}
                {(veh?.vehicles || []).length === 0 && <tr><td colSpan="11" className="py-8 text-center text-slate-400">Dönemde araç faaliyeti yok.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Personel bakiye kartı */}
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800"><Wallet className="h-4 w-4 text-brand-green" /> Personel Bakiye Kartı (Rota Satıcıları)</div>
            <table className="w-full text-sm" data-testid="van-report-salespeople">
              <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400">
                <tr><th className="px-3 py-2">Personel</th><th className="text-right">Fiş</th><th className="text-right">Ciro</th><th className="text-right">Nakit</th><th className="text-right">POS</th><th className="text-right">Havale</th><th className="text-right">Veresiye</th><th className="text-right">İskonto</th><th className="text-right">Kesinti</th></tr>
              </thead>
              <tbody>
                {(sp?.salespeople || []).map((p) => (
                  <tr key={p.salesperson_email} className="border-t border-slate-50" data-testid={`van-report-sp-${p.salesperson_email}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{p.salesperson_name || p.salesperson_email}</td>
                    <td className="text-right text-slate-600">{p.sale_count}</td>
                    <td className="text-right font-semibold text-slate-800">{fmtTL(p.gross)}</td>
                    <td className="text-right text-emerald-700">{fmtTL(p.cash)}</td>
                    <td className="text-right text-sky-700">{fmtTL(p.card)}</td>
                    <td className="text-right text-violet-700">{fmtTL(p.transfer)}</td>
                    <td className="text-right text-amber-700">{fmtTL(p.credit)}</td>
                    <td className="text-right text-slate-500">{fmtTL(p.discount_given)}</td>
                    <td className="text-right font-medium text-red-600">{p.penalty_total > 0 ? fmtTL(p.penalty_total) : "—"}</td>
                  </tr>
                ))}
                {(sp?.salespeople || []).length === 0 && <tr><td colSpan="9" className="py-8 text-center text-slate-400">Dönemde personel faaliyeti yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const tones = { emerald: "text-emerald-600 bg-emerald-50", sky: "text-sky-600 bg-sky-50", violet: "text-violet-600 bg-violet-50", amber: "text-amber-600 bg-amber-50" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-4 w-4" /></span>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <div className="mt-1.5 text-lg font-bold text-slate-900">{value}</div>
    </div>
  );
}

// ------------------ ARAÇ YÜKLEME ------------------
function VanLoad() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [stock, setStock] = useState([]);
  const [prodQ, setProdQ] = useState("");
  const [prodResults, setProdResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/van-sales/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!vehicleId) { setStock([]); return; }
    api.get(`/van-sales/vehicles/${vehicleId}/stock`).then((r) => setStock(r.data.van_stock || [])).catch(() => {});
  }, [vehicleId]);
  useEffect(() => {
    if (!prodQ.trim()) { setProdResults([]); return; }
    const t = setTimeout(() => api.get("/van-sales/products", { params: { q: prodQ, limit: 20 } }).then((r) => setProdResults(r.data || [])).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [prodQ]);

  const addStock = (p) => {
    if (stock.find((s) => s.product_id === p.id)) { toast.info("Zaten listede"); return; }
    setStock((ss) => [...ss, { product_id: p.id, sku: p.sku, name: p.name, unit: p.unit, quantity: 1 }]);
  };
  const updQty = (pid, q) => setStock((ss) => ss.map((s) => s.product_id === pid ? { ...s, quantity: q } : s));
  const rm = (pid) => setStock((ss) => ss.filter((s) => s.product_id !== pid));

  const save = async () => {
    if (!vehicleId) { toast.error("Araç seçin"); return; }
    setBusy(true);
    try {
      await api.post(`/van-sales/vehicles/${vehicleId}/load`, {
        mode: "set", lines: stock.map((s) => ({ product_id: s.product_id, quantity: Number(s.quantity) })),
      });
      toast.success("Araç stoğu güncellendi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <Label className="text-xs">Araç</Label>
        <select data-testid="van-load-vehicle" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
          <option value="">Araç seç</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate}{v.driver_name ? ` · ${v.driver_name}` : ""}</option>)}
        </select>

        {vehicleId && (
          <>
            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="van-load-search" value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Yüklenecek ürün ara…" className="pl-9" />
            </div>
            {prodResults.length > 0 && (
              <div className="mt-2 max-h-44 overflow-auto rounded-md border border-slate-100">
                {prodResults.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm last:border-0">
                    <span className="truncate">{p.name}</span>
                    <Button size="sm" variant="outline" data-testid={`van-load-add-${p.id}`} onClick={() => addStock(p)}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-2" data-testid="van-load-list">
              {stock.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Araç boş. Ürün ekleyin.</p>}
              {stock.map((s) => (
                <div key={s.product_id} className="flex items-center gap-2" data-testid={`van-load-row-${s.product_id}`}>
                  <Package className="h-4 w-4 shrink-0 text-slate-300" />
                  <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{s.name}{s.low && <span className="ml-1 rounded bg-red-100 px-1 text-[10px] font-medium text-red-700">kritik</span>}</span>
                  <Input type="number" min="0" step="1" value={s.quantity} onChange={(e) => updQty(s.product_id, e.target.value)} className="h-8 w-24" data-testid={`van-load-qty-${s.product_id}`} />
                  <span className="text-xs text-slate-400">{s.unit}</span>
                  <button onClick={() => rm(s.product_id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <Button className="mt-4 w-full" onClick={save} disabled={busy} data-testid="van-load-save">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Truck className="mr-1 h-4 w-4" />} Araç Stoğunu Kaydet
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
